<template>
  <!--
    Top-level row in the grouped PMA view. One row per distinct
    company_name. Emits raw <tr>/<td> so widths come from the outer
    table's <colgroup> and stay aligned no matter what's expanded.

    Visual hierarchy:
      - Collapsed: plain row, hover:bg-gray-50.
      - Expanded: same row gets a subtle accent tint + the expand
        triangle rotates. Below it, a single <tr> hosts a flush panel
        of make accordions (no card-within-card, no noisy header).
  -->
  <tr
    :class="[
      'border-b border-gray-100 align-top',
      expanded ? 'bg-accent-primary/10' : 'hover:bg-gray-50',
    ]"
  >
    <td class="px-2 py-1.5 text-center">
      <input
        type="checkbox"
        :checked="checked"
        :disabled="!linkedCompanies.length"
        :title="linkedCompanies.length ? 'Select this company group for bulk ops' : 'No linked Companies to operate on'"
        @change="$emit('toggle-checked', company.company_name)"
        class="cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
      />
    </td>
    <td class="px-2 py-1.5">
      <button
        @click="toggleExpanded"
        class="w-full text-dark-primary hover:text-accent-primary font-bold leading-none"
        :aria-expanded="expanded"
      >
        {{ expanded ? "▼" : "▶" }}
      </button>
    </td>
    <td class="px-2 py-1.5 truncate text-black font-medium" :title="company.company_name">
      {{ company.company_name }}
    </td>
    <td class="px-2 py-1.5 truncate text-black" :title="fullAddress">
      {{ fullAddress || "—" }}
    </td>
    <td class="px-2 py-1.5 text-black font-semibold">{{ company.pma_count }}</td>
    <td class="px-2 py-1.5">
      <div v-if="linkedCompanies.length === 0" class="text-xs text-gray-400">—</div>
      <ul v-else class="text-xs text-black space-y-0.5">
        <li
          v-for="linked in linkedCompanies"
          :key="linked.id"
          @click="openLinkedCompanyModal(linked)"
          :class="[
            { 'hover:cursor-pointer hover:underline': !isLoading },
            linked.status === 'inactive' ? 'text-gray-400' : 'text-indigo-600',
          ]"
          class="truncate"
          :title="linked.status === 'inactive'
            ? `${linked.company_name} (ID: ${linked.id}) — inactive`
            : `${linked.company_name} (ID: ${linked.id})`"
        >
          {{ linked.company_name }} <span class="text-gray-500">(ID: {{ linked.id }})</span>
          <span v-if="linked.status === 'inactive'" class="text-gray-400 italic">(inactive)</span>
        </li>
      </ul>
    </td>
  </tr>

  <!--
    Expansion row. Hosts the makes panel as a single full-width cell.
    The panel itself uses div/section markup so the outer column grid
    is unaffected by anything happening inside.
  -->
  <tr v-if="expanded" class="bg-gray-50">
    <td colspan="6" class="p-0">
      <div class="pl-12 pr-4 py-2">
        <div v-if="makesLoading" class="text-xs text-gray-600 py-1">
          Loading makes…
        </div>
        <div v-else-if="makes.length === 0" class="text-xs text-gray-600 py-1">
          No makes found for this company under the current filters.
        </div>
        <div v-else class="divide-y divide-gray-200">
          <PMAMakeRow
            v-for="make in makes"
            :key="make.make"
            :make="make"
            :companyName="company.company_name"
            :filterQuery="filterQuery"
            :resetObject="resetObject"
          ></PMAMakeRow>
        </div>
      </div>
    </td>
  </tr>

  <!--
    Edit modal for a clicked linked Company. Mirrors PMACompanyCard's
    flow: fetch the full Company via getCompanyByID on click, hand it
    to EditCompany, and refetch the company list when the user makes
    edits that may change names / locations the parent row displays.
  -->
  <base-modal :is-form-visible="linkedCompanyModalVisible" @close="closeLinkedCompanyModal">
    <template #header>
      <h2 v-if="selectedFullCompany" class="text-lg">
        {{ selectedFullCompany.company_name }} {{ selectedFullCompany.id }}
      </h2>
    </template>
    <template #body>
      <EditCompany
        v-if="selectedFullCompany"
        :company="selectedFullCompany"
        :campaigns="campaigns"
        @close="closeLinkedCompanyModal"
        @refetch-companies="emitRefreshData"
      />
    </template>
  </base-modal>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import { getPMACompanyMakes } from "@/services/PMAList.js";
import { getCompanyByID } from "@/services/Company";
import PMAMakeRow from "./PMAMakeRow.vue";
import EditCompany from "@/components/EditCompany.vue";

const props = defineProps({
  company: { type: Object, required: true },
  filterQuery: { type: String, default: "?" },
  resetObject: { type: Object, required: true },
  campaigns: { type: Array, default: () => [] },
  isLoading: { type: Boolean, default: false },
  checked: { type: Boolean, default: false },
});
const emit = defineEmits(["set-loading", "refresh-data", "toggle-checked"]);
const store = useStore();
const $toast = useToast();

const expanded = ref(false);
const makes = ref([]);
const makesLoaded = ref(false);
const makesLoading = ref(false);

const fullAddress = computed(() => {
  const parts = [
    props.company.address,
    [props.company.city, props.company.state].filter(Boolean).join(", "),
    props.company.zip,
    props.company.country,
  ].filter((p) => p && p.toString().trim() !== "");
  return parts.join(" ");
});

const linkedCompanies = computed(() => props.company.linked_companies || []);

async function toggleExpanded() {
  expanded.value = !expanded.value;
  if (expanded.value && !makesLoaded.value) {
    await fetchMakes();
  }
}

function buildMakesEndpoint() {
  const base = props.filterQuery || "?";
  const joiner = base === "?" ? "" : "&";
  const safeName = encodeURIComponent(props.company.company_name);
  return `${base}${joiner}selected_company_name=${safeName}`;
}

async function fetchMakes() {
  makesLoading.value = true;
  try {
    const response = await getPMACompanyMakes({
      token: store.state.auth.token,
      endpoint: buildMakesEndpoint(),
    });
    makes.value = response.makes;
    makesLoaded.value = true;
  } catch (err) {
    console.error("Failed to load makes for", props.company.company_name, err);
    makes.value = [];
  } finally {
    makesLoading.value = false;
  }
}

watch(
  () => props.resetObject.buttonHit,
  () => {
    expanded.value = false;
    makes.value = [];
    makesLoaded.value = false;
  }
);

// --- Linked-company modal ---

const linkedCompanyModalVisible = ref(false);
const selectedFullCompany = ref(null);

async function openLinkedCompanyModal(linked) {
  if (props.isLoading) return;
  emit("set-loading", true);
  try {
    const result = await getCompanyByID({
      token: store.state.auth.token,
      companyId: linked.id,
    });
    if (result.response.status === 200) {
      selectedFullCompany.value = result.response.data;
      linkedCompanyModalVisible.value = true;
    } else {
      $toast.open({
        message: "Error, could not fetch company",
        type: "error",
        duration: 3000,
      });
    }
  } finally {
    emit("set-loading", false);
  }
}

function closeLinkedCompanyModal() {
  linkedCompanyModalVisible.value = false;
  selectedFullCompany.value = null;
}

function emitRefreshData() {
  emit("refresh-data");
}
</script>
